import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import { getRememberedSettingsCustomer } from '@/settingsContext'
import { useSiteSettings } from '@/siteSettingsContext'

export function FieldNamesSettings() {
  const { isAdmin } = useAuth()
  const { siteTitle, customerLabel, logoText, applySettings, refresh } =
    useSiteSettings()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [draftTitle, setDraftTitle] = useState(siteTitle)
  const [draftCustomerLabel, setDraftCustomerLabel] = useState(customerLabel)
  const [draftLogoText, setDraftLogoText] = useState(logoText)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setDraftTitle(siteTitle)
    setDraftCustomerLabel(customerLabel)
    setDraftLogoText(logoText)
  }, [siteTitle, customerLabel, logoText])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const title = draftTitle.trim()
    const label = draftCustomerLabel.trim()
    const logo = draftLogoText.trim()
    if (!title) {
      setError('Site title cannot be empty.')
      return
    }
    if (!label) {
      setError('Customer label cannot be empty.')
      return
    }
    if (!logo) {
      setError('Logo text cannot be empty.')
      return
    }
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const updated = await settingsApi.updateSiteSettings({
        site_title: title,
        customer_label: label,
        logo_text: logo,
      })
      applySettings(updated)
      await refresh()
      setSaved(true)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  const previewLogo = (draftLogoText || 'BK').slice(0, 4).toUpperCase()

  return (
    <SettingsShell title="Field names" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">
            Field names
          </h1>
          <p className="mt-2 max-w-2xl text-brand-muted">
            Labels used across the knowledge base. Change these when the KB is
            used for a subject other than IT.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm"
        >
          <label className="block max-w-md">
            <span className="text-sm font-medium text-brand">Logo text</span>
            <p className="mt-1 text-xs text-brand-muted">
              Short initials shown in the blue tile (up to 4 characters), like BD
              on Bekat Dash.
            </p>
            <div className="mt-2 flex items-center gap-3">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0B5FFF] text-sm font-bold text-white"
                aria-hidden
              >
                {previewLogo}
              </span>
              <input
                className="w-24 rounded-md border border-slate-300 px-3 py-2 uppercase text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                value={draftLogoText}
                onChange={(e) => {
                  setDraftLogoText(e.target.value.slice(0, 4))
                  setSaved(false)
                }}
                maxLength={4}
                autoComplete="off"
              />
            </div>
          </label>

          <label className="block max-w-md">
            <span className="text-sm font-medium text-brand">Site title</span>
            <p className="mt-1 text-xs text-brand-muted">
              Shown beside the logo, on the sign-in page, and in the browser tab.
            </p>
            <input
              className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              value={draftTitle}
              onChange={(e) => {
                setDraftTitle(e.target.value)
                setSaved(false)
              }}
              maxLength={120}
              autoComplete="off"
            />
          </label>

          <label className="block max-w-md">
            <span className="text-sm font-medium text-brand">Customer label</span>
            <p className="mt-1 text-xs text-brand-muted">
              Sidebar heading above the organisation picker (for example Customer,
              Client, or School).
            </p>
            <input
              className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              value={draftCustomerLabel}
              onChange={(e) => {
                setDraftCustomerLabel(e.target.value)
                setSaved(false)
              }}
              maxLength={120}
              autoComplete="off"
            />
          </label>

          {error && (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          )}
          {saved && !error && (
            <p className="text-sm text-emerald-700">Saved.</p>
          )}

          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      </div>
    </SettingsShell>
  )
}
